import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, Sparkles, TrendingUp, Lock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { pushEvent } from "@/lib/datalayer";

interface SampleStock {
  ticker: string;
  name: string;
  riskLevel: string;
  allocation: number;
  currentPrice: number;
  confidence?: number;
}

interface SampleRow {
  run_date: string;
  amount: number;
  payload: { stocks?: SampleStock[]; summary?: string };
}

const riskColor = (r: string) =>
  r === "low"
    ? "text-emerald-500 border-emerald-500/40 bg-emerald-500/10"
    : r === "high"
    ? "text-red-500 border-red-500/40 bg-red-500/10"
    : "text-amber-500 border-amber-500/40 bg-amber-500/10";

const HeroSampleCard = () => {
  const [row, setRow] = useState<SampleRow | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("sample_analyses")
        .select("run_date, amount, payload")
        .order("run_date", { ascending: false })
        .limit(1)
        .maybeSingle();
      setRow(data as any);
      setLoading(false);
    })();
  }, []);

  const stocks = (row?.payload?.stocks ?? []).slice(0, 3);
  const totalStocks = row?.payload?.stocks?.length ?? 0;
  const remaining = Math.max(totalStocks - stocks.length, 0);

  const dateStr = row
    ? new Date(row.run_date + "T00:00:00").toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
      })
    : "";

  return (
    <div className="relative rounded-2xl border-2 border-foreground bg-card shadow-brutal overflow-hidden">
      <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full halo-mint pointer-events-none" />

      <div className="relative p-5 sm:p-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="space-y-2 min-w-0">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border-2 border-foreground bg-accent shadow-brutal-sm">
              <Sparkles className="w-3 h-3" />
              <span className="text-[10px] uppercase tracking-wider font-bold font-body">
                Free daily sample
              </span>
            </div>
            <h3 className="font-display text-xl sm:text-2xl font-bold tracking-tight leading-tight">
              Today's AI Buy signals
            </h3>
            {row && (
              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground font-body">
                <Calendar className="w-3 h-3" />
                <span>{dateStr}</span>
                <span className="text-border">·</span>
                <span>$10K · long-term</span>
              </div>
            )}
          </div>
        </div>

        {/* Summary */}
        {row?.payload?.summary && (
          <div className="mb-4 rounded-lg border border-border bg-secondary/30 p-3">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1 font-body">
              <TrendingUp className="w-3 h-3" /> Market view
            </div>
            <p className="text-xs text-foreground/90 leading-relaxed line-clamp-2">
              {row.payload.summary}
            </p>
          </div>
        )}

        {/* Stocks */}
        <div className="space-y-2 mb-4">
          {loading
            ? Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-16 rounded-lg border-2 border-foreground bg-background/40 animate-pulse" />
              ))
            : stocks.map((s, i) => {
                const dollars = Math.round((row?.amount ?? 0) * (s.allocation || 0) / 100);
                return (
                  <motion.article
                    key={s.ticker}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: i * 0.05 }}
                    className="rounded-lg border-2 border-foreground bg-background p-3 shadow-brutal-sm"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2">
                          <span className="font-display text-sm font-bold truncate">{s.name}</span>
                          <span className="text-[10px] text-muted-foreground font-mono shrink-0">{s.ticker}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${riskColor(s.riskLevel)}`}>
                            {s.riskLevel}
                          </span>
                          <span className="text-[10px] text-muted-foreground font-body">
                            {s.allocation}% · ~${dollars.toLocaleString()}
                          </span>
                        </div>
                      </div>
                      <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-md border-2 border-foreground bg-primary text-primary-foreground shrink-0">
                        Buy
                      </span>
                    </div>
                  </motion.article>
                );
              })}
        </div>

        {/* Locked teaser strip */}
        {remaining > 0 && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3 px-1 font-body">
            <Lock className="w-3 h-3" />
            <span>+ {remaining} more AI Buy signals · full reasoning & ROI</span>
          </div>
        )}

        {/* CTA */}
        <Link
          to="/sample-analysis"
          onClick={() =>
            pushEvent({ event: "click_get_started" as any, page_type: "home", cta_location: "hero_sample_card" as any })
          }
          className="group w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-primary text-primary-foreground font-bold border-2 border-foreground shadow-brutal-sm hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-brutal active:translate-x-[1px] active:translate-y-[1px] active:shadow-none transition-all duration-150 font-body text-sm"
        >
          See full sample analysis
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </div>
    </div>
  );
};

export default HeroSampleCard;
